import { GridRowModes } from '@mui/x-data-grid-pro';
import { isRowCrediting } from '../HoursTable.utils';
import { CreditOperation } from '../HoursTable.enums';
import {
  CustomDutyLine,
  DraftDutyLine
} from 'RestAPIs/employees/hours-table/types';
import { useCallback } from 'react';
import { RowLevelDeps } from '../HoursTable.types';

export function useRowLevelActions({
  apiRef,
  employeeId,
  isEmployeePortal,
  mutateFlags,
  creditDutyLine,
  flushBatch,
  setRowModesModel,
  setEditingRowId,
  setIsRowEditModeActive,
  setExtraRows,
  setCopyMode,
  createEmptyDutyLine
}: RowLevelDeps) {
  const handleBlockToggle = useCallback(
    async (row: CustomDutyLine) => {
      if (!row.requestId) return;
      const blocked = row.flags?.includes('STATUS_BLOCKED');
      await mutateFlags({
        employeeId,
        isEmployeePortal,
        requestId: row.requestId,
        flags: ['STATUS_BLOCKED'],
        operation: blocked ? 'remove' : 'add'
      });
    },
    [employeeId, isEmployeePortal, mutateFlags]
  );

  const handleCreditingToggle = useCallback(
    async (row: CustomDutyLine) => {
      if (!row.requestId) return;
      await creditDutyLine({
        employeeId,
        isEmployeePortal,
        requestId: row.requestId,
        operation: isRowCrediting(row)
          ? CreditOperation.UNDO_CREDITING
          : CreditOperation.CREDITING
      });
    },
    [employeeId, isEmployeePortal, creditDutyLine]
  );

  const handleNominalHoursToggle = useCallback(
    (row: CustomDutyLine) => {
      if (row.requestId) return; // only on drafts
      if (row.isNominalRow) {
        setRowModesModel((m) => ({
          ...m,
          [row.id]: { mode: GridRowModes.Edit, fieldToFocus: 'nominalHours' }
        }));
        setEditingRowId(row.id);
        setIsRowEditModeActive(true);
        return;
      }
      const draft: DraftDutyLine = { ...row, isNominalRow: true };
      apiRef?.current?.updateRows([draft]);
      setRowModesModel((m) => ({
        ...m,
        [draft.id]: { mode: GridRowModes.Edit, fieldToFocus: 'dvb' }
      }));
      setEditingRowId(draft.id);
      setIsRowEditModeActive(true);
    },
    [apiRef, setRowModesModel, setEditingRowId, setIsRowEditModeActive]
  );

  const handleAddRow = useCallback(
    (templateRow: CustomDutyLine | DraftDutyLine) => {
      const blank = createEmptyDutyLine(templateRow);
      setExtraRows((prev) => [...prev, blank]);
    },
    [createEmptyDutyLine, setExtraRows]
  );

  const handleFinishCopy = useCallback(async () => {
    await flushBatch();
    setCopyMode(false);
    setIsRowEditModeActive(false);
  }, [flushBatch, setCopyMode, setIsRowEditModeActive]);

  return {
    handleBlockToggle,
    handleCreditingToggle,
    handleNominalHoursToggle,
    handleAddRow,
    handleFinishCopy
  };
}
